import React, { useEffect, useState } from "react";
import { db, auth, storage } from "../Context/firebaseConfig";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useAuth } from "../Context/AuthContext";
import { Dialog, Transition } from "@headlessui/react";
import DOMPurify from "dompurify";
import sanitizeFilename from "sanitize-filename";
import {
  ArrowLeft,
  Package,
  Paperclip,
  Send,
} from "lucide-react";
import { splitMessageText } from "../utils/messageLinks";
import { Link } from "react-router-dom";
import { validateOrderDraft } from "../utils/orderDraft";
import { calculateVideoOrderCharge } from "../utils/videoOrderFees";
import { getSafeExternalUrl, sanitizePlainText } from "../utils/security";
import {
  queueTransactionalEmail,
  transactionalEmails,
} from "../utils/transactionalEmail";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const MAX_CHAT_VIDEO_FILES = 8;
const MAX_CHAT_VIDEO_FILE_SIZE = 300 * 1024 * 1024;
const CHAT_VIDEO_EXTENSIONS = [".mp4", ".mov"];

const MessageText = ({ text, isCurrentUser }) => (
  <>
    {splitMessageText(text).map((part, index) =>
      part.isLink ? (
        <a
          key={`${part.text}-${index}`}
          href={part.url}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          className={`break-all font-semibold underline underline-offset-2 ${
            isCurrentUser
              ? "decoration-black/40 hover:decoration-black"
              : "text-emerald-300 decoration-emerald-300/50 hover:text-emerald-200"
          }`}
        >
          {part.text}
        </a>
      ) : (
        <React.Fragment key={`${part.text}-${index}`}>{part.text}</React.Fragment>
      )
    )}
  </>
);

const HeaderAvatar = ({ src, name }) => {
  const initial = (name || "?").charAt(0).toUpperCase();

  return (
    <div
      className="h-10 w-10 shrink-0 overflow-hidden rounded-full"
      style={{
        background: `conic-gradient(from 140deg, ${PURPLE}, ${MINT}, ${PURPLE})`,
        padding: 1.5,
      }}
    >
      <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#0b0a14] text-xs font-semibold text-white/80">
        {src ? (
          <img src={src} alt={name} className="h-full w-full object-cover" />
        ) : (
          initial
        )}
      </div>
    </div>
  );
};

const ChatWindow = ({
  messages,
  currentUser,
  userDetails,
  selectedChat,
  initialDraft = "",
  onBack,
}) => {
  const [newMessage, setNewMessage] = useState(initialDraft);
  const [open, setOpen] = useState(true);
  const [orderTitle, setOrderTitle] = useState("");
  const [orderDescription, setOrderDescription] = useState("");
  const [orderPrice, setOrderPrice] = useState("");
  const [status, setStatus] = useState("pending");
  // const [questionOne, setQuestionOne] = useState("");
  // const [questionTwo, setQuestionTwo] = useState("");
  // const [questionThree, setQuestionThree] = useState("");
  // const [questionFour, setQuestionFour] = useState("");
  const [daysNeeded, setDaysNeeded] = useState("");
  const [orderErrors, setOrderErrors] = useState({});
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [isDaysModalOpen, setIsDaysModalOpen] = useState(false);
  const [isGifModalOpen, setIsGifModalOpen] = useState(false);
  const [gifSearchTerm, setGifSearchTerm] = useState("");
  const [selectedVideoFiles, setSelectedVideoFiles] = useState([]);
  const [videoFileError, setVideoFileError] = useState("");
  const [isUploadingVideos, setIsUploadingVideos] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [fileInputResetKey, setFileInputResetKey] = useState(0);
  const { activeUser } = useAuth();

  useEffect(() => {
    setNewMessage(initialDraft);
  }, [initialDraft, selectedChat]);

  if (!currentUser) return null;

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const openGifModal = () => {
    setIsGifModalOpen(true);
  };

  async function lookupEmailByUid(uid) {
    const snap = await getDoc(doc(db, "Users", uid));
    if (!snap.exists()) throw new Error("User not found: " + uid);
    return snap.data().profileInfo?.email;
  }

  if (!selectedChat) {
    return <></>; // hide chat on mobile if no conversation selected
  }

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const uploadChatVideo = (file, index, totalFiles) =>
    new Promise((resolve, reject) => {
      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFilename(file.name) || `video-${index}`;
      const storagePath = `chat_uploads/${selectedChat}/${currentUser.uid}/${timestamp}_${index}_${sanitizedFileName}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          chatId: selectedChat,
          senderId: currentUser.uid,
          originalFileName: file.name,
          uploadedAt: new Date().toISOString(),
        },
      });

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const fileProgress = snapshot.bytesTransferred / snapshot.totalBytes;
          const overallProgress =
            ((index + fileProgress) / totalFiles) * 100;
          setVideoUploadProgress(Math.round(overallProgress));
        },
        reject,
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            resolve({
              id: `${selectedChat}_${timestamp}_${index}`,
              name: file.name,
              size: file.size,
              type: file.type,
              url: downloadURL,
              storagePath,
              uploadedAt: new Date().toISOString(),
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });

  const handleSendMessage = async () => {
    const trimmedMessage = sanitizePlainText(newMessage, { maxLength: 5000 });
    const hasVideos = selectedVideoFiles.length > 0;

    if ((trimmedMessage === "" && !hasVideos) || isUploadingVideos) return;

    try {
      setVideoFileError("");
      setIsUploadingVideos(hasVideos);
      setVideoUploadProgress(hasVideos ? 0 : 100);

      const uploadedVideos = [];

      if (hasVideos) {
        for (let index = 0; index < selectedVideoFiles.length; index += 1) {
          const uploadedVideo = await uploadChatVideo(
            selectedVideoFiles[index],
            index,
            selectedVideoFiles.length
          );
          uploadedVideos.push(uploadedVideo);
        }
      }

      const lastMessageText =
        trimmedMessage ||
        `Sent ${uploadedVideos.length} video${uploadedVideos.length === 1 ? "" : "s"}`;

      const messageData = {
        senderId: currentUser.uid,
        text: trimmedMessage,
        timestamp: Date.now(),
        type: uploadedVideos.length > 0 ? "video" : "text",
        ...(uploadedVideos.length > 0 ? { attachments: uploadedVideos } : {}),
      };

      const chatDocRef = doc(db, "chats", selectedChat);
      await updateDoc(chatDocRef, {
        messages: arrayUnion(messageData),
        lastMessage: lastMessageText,
        timestamp: new Date(),
      });
      const userChatRef = doc(
        db,
        "Users",
        currentUser.uid,
        "chats",
        selectedChat
      );
      const friendChatRef = doc(db, "Users", friendId, "chats", selectedChat);

      await Promise.all([
        updateDoc(userChatRef, {
          lastMessage: lastMessageText,
          timestamp: serverTimestamp(),
        }),
        updateDoc(friendChatRef, {
          lastMessage: lastMessageText,
          timestamp: serverTimestamp(),
        }),
      ]);
      // Send a notification separately
      await sendNotificationSimple(selectedChat);
      const recipientEmail = await lookupEmailByUid(friendId);
      await queueTransactionalEmail({
        to: recipientEmail,
        email: transactionalEmails.chatActivity({
          senderName: activeUser?.profileInfo?.username || "A Kre8 member",
          conversationId: selectedChat,
        }),
      });

      setNewMessage("");
      setSelectedVideoFiles([]);
      setVideoUploadProgress(0);
      setFileInputResetKey((key) => key + 1);
    } catch (error) {
      console.error("Error sending message:", error);
      setVideoFileError("Failed to send message. Please try again.");
    } finally {
      setIsUploadingVideos(false);
    }
  };

  const handleVideoFileChange = (event) => {
    const files = Array.from(event.target.files || []);

    setVideoFileError("");

    if (files.length === 0) {
      setSelectedVideoFiles([]);
      return;
    }

    if (files.length > MAX_CHAT_VIDEO_FILES) {
      setVideoFileError(`You can attach up to ${MAX_CHAT_VIDEO_FILES} videos at once.`);
      setSelectedVideoFiles([]);
      event.target.value = "";
      return;
    }

    const invalidType = files.find((file) => {
      const lowerName = file.name.toLowerCase();
      return !CHAT_VIDEO_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
    });

    if (invalidType) {
      setVideoFileError("Only .mp4 and .mov video files are supported.");
      setSelectedVideoFiles([]);
      event.target.value = "";
      return;
    }

    const oversizedFile = files.find((file) => file.size > MAX_CHAT_VIDEO_FILE_SIZE);

    if (oversizedFile) {
      setVideoFileError("Each video must be 300 MB or smaller.");
      setSelectedVideoFiles([]);
      event.target.value = "";
      return;
    }

    setSelectedVideoFiles(files);
  };

  const sendNotificationSimple = async (chatId) => {
    try {
      const participants = chatId.split("_");
      const receiverId = participants.find((id) => id !== auth.currentUser.uid);

      if (!receiverId) {
        console.error("Receiver ID not found.");
        return;
      }

      const receiverDocRef = doc(db, "Users", receiverId);
      const receiverDocSnap = await getDoc(receiverDocRef);

      if (!receiverDocSnap.exists()) {
        console.error("Receiver user document does not exist.");
        return;
      }

      const newNotification = {
        title: "New Message",
        body: "You have a new message!",
        navigationLink: `/chat?conversationId=${chatId}`,
      };

      setDoc(
        receiverDocRef,
        {
          notifications: arrayUnion(newNotification),
        },
        { merge: true }
      );

      console.log(" Notification added successfully.");
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const friendId = currentUser
    ? selectedChat.split("_").find((id) => id !== currentUser.uid)
    : null;

  // const friendId = selectedChat
  //   ?.split("_")
  //   .find((id) => id !== currentUser.uid);
  console.log("Friend ID:", friendId);
  const friendDetails = userDetails[friendId] || {};
  console.log("DETAILS", friendDetails)
  const currentUserDetails = userDetails[currentUser.uid] || {};
  const isEditor = activeUser?.profileInfo?.role?.toLowerCase() === "editor";
  const friendUsername = friendDetails.profileInfo?.username || "";
  const friendRole = friendDetails.profileInfo?.role?.toLowerCase();
  const friendProfilePath = friendUsername
    ? `/${friendRole === "creator" ? "creator" : "editor"}/${friendUsername}`
    : "";

  function generateOrderId() {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < 13) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  const orderNumber = generateOrderId();

  const currentDate = new Date(Date.now());

  let month = currentDate.getMonth() + 1;
  let day = currentDate.getDate();
  const year = currentDate.getFullYear();

  month = month < 10 ? "0" + month : month;
  day = day < 10 ? "0" + day : day;

  const formattedDate = `${month}/${day}/${year}`;

  function calculateDeliveryDate(daysNeeded) {
    const deliveryDate = new Date(currentDate);
    deliveryDate.setDate(deliveryDate.getDate() + daysNeeded);
    let month = deliveryDate.getMonth() + 1;
    let day = deliveryDate.getDate();
    const year = deliveryDate.getFullYear();
    month = month < 10 ? "0" + month : month;
    day = day < 10 ? "0" + day : day;
    const formattedDeliveryDate = `${month}/${day}/${year}`;
    return formattedDeliveryDate;
  }

  async function checkInputStrLength(
    orderTitle,
    orderDescription,
    orderPrice,
    daysNeeded
  ) {
    const validation = validateOrderDraft({
      title: orderTitle,
      description: orderDescription,
      price: orderPrice,
      daysNeeded,
    });
    setOrderErrors(validation.errors);
    if (!validation.isValid) {
      toast.error("Check the highlighted order details.");
      return false;
    }

    await startOrder(
      validation.value.title,
      validation.value.description,
      validation.value.price,
      validation.value.daysNeeded
    );
    return true;
  }

  const startOrder = async (
    orderTitle,
    orderDescription,
    orderPrice,
    daysNeeded
  ) => {
    if (isCreatingOrder) return;
    setIsCreatingOrder(true);

    const initialPrice = parseFloat(orderPrice);

    if (isNaN(initialPrice)) {
      setOrderErrors((previous) => ({ ...previous, price: "Enter a valid order price." }));
      setIsCreatingOrder(false);
      return;
    }
    try {
      if (!friendId || !currentUser?.uid) {
        throw new Error("The other account is still loading. Close the order form and try again.");
      }

      const charge = calculateVideoOrderCharge(initialPrice);
      const orderData = {
        orders: {
          pending: {
            [orderNumber]: {
              title: orderTitle,
              description: orderDescription,
              price: {
                initial: charge.editorPrice,
                fees: charge.fees,
                final: charge.total,
                platformFee: charge.platformFee,
                processingFee: charge.processingFee,
              },
              orderNumber,
              creatorId: friendId,
              editorId: currentUser.uid,
              status,
              timestamp: formattedDate,
              daysNeeded,
              dueDate: calculateDeliveryDate(daysNeeded),
              editorEmail: currentUser.email || "",
              creatorEmail: friendDetails.profileInfo?.email || "",
            },
          },
        },
      };

      const orderMessage = {
        senderId: currentUser.uid,
        text: "A new order has been submitted, please view the order details - Kre8 Content (Refer a friend, get 15% off and points for more discounts + merch).",
        timestamp: formattedDate,
        type: "order",
        orderReferenceNumber: orderNumber,
      };

      const orderLinkMessage = {
        senderId: currentUser.uid,
        text: `View Order Details. Be sure to send a Google Drive link containing the files you want to be edited if you have multiple clips and not just 1!`,
        timestamp: formattedDate,
        type: "orderLink",
        orderReferenceNumber: orderNumber,
      };

      const currentUserDocRef = doc(db, "Users", currentUser.uid);
      await setDoc(currentUserDocRef, orderData, { merge: true });

      console.log("Order data successfully set for current user.");

      const friendDocRef = doc(db, "Users", friendId);
      await setDoc(friendDocRef, orderData, { merge: true });

      await sendOrderMessageData(orderMessage, orderLinkMessage, selectedChat);
      closePopup();
      setOrderTitle("");
      setOrderDescription("");
      setOrderPrice("");
      setDaysNeeded("");
      setOrderErrors({});
      toast.success("Order created successfully.");
    } catch (error) {
      console.error("Error setting order data:", error);
      toast.error(error?.message || "Could not create the order. Please try again.");
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const sendOrderMessageData = async (message, linkMessage, selectedChat) => {
    try {
      // const db = getDatabase();
      // const chatRef = ref(db, `chatMessages/${selectedChat}/messages`);
      // await push(chatRef, message);
      // await push(chatRef, linkMessage);
      // -----the issue is here, please fix it-----
      const chatDocRef = doc(db, "chats", selectedChat);
      await updateDoc(chatDocRef, {
        messages: arrayUnion(message, linkMessage),
        lastMessage: linkMessage.text,
        timestamp: new Date(),
      });

      const userChatRef = doc(
        db,
        "Users",
        currentUser.uid,
        "chats",
        selectedChat
      );
      const friendChatRef = doc(db, "Users", friendId, "chats", selectedChat);

      await Promise.all([
        updateDoc(userChatRef, {
          lastMessage: linkMessage.text,
          timestamp: serverTimestamp(),
        }),
        updateDoc(friendChatRef, {
          lastMessage: linkMessage.text,
          timestamp: serverTimestamp(),
        }),
      ]);
    } catch (error) {
      console.error("Error pushing messages to Realtime DB:", error);
    }
  };

  const renderMessage = (message, index) => {
    const isCurrentUser = message.senderId === currentUser.uid;

    if (message.type === "order") {
      return (
        <div
          key={index}
          className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
        >
          <div
            className="max-w-[85%] rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-white backdrop-blur-xl sm:max-w-[70%]"
            style={{
              boxShadow: `0 12px 40px -20px ${PURPLE}, inset 0 1px 0 0 rgba(255,255,255,0.04)`,
            }}
          >
            <div className="mb-2 flex items-center gap-2">
              <span
                className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 bg-white/5"
                style={{ color: PURPLE }}
              >
                <Package size={13} />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-white/50">
                  Order
                </p>
                <p className="truncate text-sm font-semibold text-white">
                  New order
                </p>
              </div>
            </div>
            <p className="leading-relaxed text-white/80">{message.text}</p>
          </div>
        </div>
      );
    }

    if (message.type === "orderLink") {
      return (
        <div
          key={index}
          className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
        >
          <div
            className="max-w-[85%] rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm text-white backdrop-blur-xl sm:max-w-[70%]"
            style={{
              boxShadow: `0 12px 40px -20px ${MINT}, inset 0 1px 0 0 rgba(255,255,255,0.04)`,
            }}
          >
            <button
              type="button"
              onClick={() =>
                (window.location.href = `/order/${encodeURIComponent(
                  message.orderReferenceNumber
                )}`)
              }
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/10"
            >
              {message.text}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        key={index}
        className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
      >
        <div
          className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed transition hover:brightness-110 sm:max-w-[70%] ${
            isCurrentUser
              ? "text-black"
              : "border border-white/10 bg-white/[0.04] text-white backdrop-blur-xl"
          }`}
          style={
            isCurrentUser
              ? {
                  background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                  boxShadow: `0 10px 28px -14px ${MINT}`,
                }
              : undefined
          }
        >
          {message.text && (
            <div>
              <MessageText
                text={message.text}
                isCurrentUser={isCurrentUser}
              />
            </div>
          )}
          {Array.isArray(message.attachments) && message.attachments.length > 0 && (
            <div className={message.text ? "mt-3 space-y-2" : "space-y-2"}>
              {message.attachments.map((file, attachmentIndex) => (
                <a
                  key={file.id || `${file.url}-${attachmentIndex}`}
                  href={getSafeExternalUrl(file.url) || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className={`block rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                    isCurrentUser
                      ? "border-black/10 bg-black/10 text-black hover:bg-black/15"
                      : "border-white/10 bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  <span className="block truncate">{file.name}</span>
                  <span
                    className={`mt-0.5 block text-[10px] font-normal ${
                      isCurrentUser ? "text-black/50" : "text-white/40"
                    }`}
                  >
                    {file.type || "Video"}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Transition show={isTitleModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-[70]"
          onClose={() => setIsTitleModalOpen(false)}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <svg
                        className="h-6 w-6 text-red-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Order Title Must Be Be Longer Than 10 Characters
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={() => setIsTitleModalOpen(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition show={isDescriptionModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-[70]"
          onClose={() => setIsDescriptionModalOpen(false)}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <svg
                        className="h-6 w-6 text-red-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Order Description Too Short
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Order description should be at least 200 characters
                          and maximum 250.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full bg-purple-600 justify-center rounded-md border px-4 py-2 text-base font-medium text-white shadow-sm bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                      onClick={() => setIsDescriptionModalOpen(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Price Modal */}
      <Transition show={isPriceModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-[70]"
          onClose={() => setIsPriceModalOpen(false)}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <svg
                        className="h-6 w-6 text-red-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Order Price Too Low
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Order price should be at least 1 USD.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={() => setIsPriceModalOpen(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Days Needed Modal */}
      <Transition show={isDaysModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-[70]"
          onClose={() => setIsDaysModalOpen(false)}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <svg
                        className="h-6 w-6 text-red-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Delivery Window Too Long
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Days needed must be higher than 0 and less than 7.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={() => setIsDaysModalOpen(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* GIF Search Modal */}
      <Transition show={isGifModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsGifModalOpen(false)}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search GIFs..."
                      value={gifSearchTerm}
                      onChange={(e) => setGifSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    />
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {gifSearchTerm && (
                      <Grid width={800} columns={2} gutter={3} fetchGifs={fetchGifs} key={gifSearchTerm} />
                    )}
                  </div>
                  <div className="mt-6 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => setIsGifModalOpen(false)}
                      className="text-sm font-semibold leading-6 text-gray-900"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div> */}
        </Dialog>
      </Transition>

      {isPopupOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closePopup}
          />
          <div className="relative flex min-h-full items-start justify-center p-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] sm:items-center sm:p-4">
            <div className="relative max-h-[calc(100dvh-24px)] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-[#0b0a14] p-5 text-left shadow-2xl shadow-purple-950/30">
              <form
                noValidate
                onSubmit={async (e) => {
                  e.preventDefault();
                  await checkInputStrLength(
                    orderTitle,
                    orderDescription,
                    orderPrice,
                    daysNeeded
                  );
                }}
              >
                <div className="space-y-8">
                  <div className="border-b border-white/10 pb-8">
                    <div className="text-center">
                      <div
                        className="mx-auto mb-4 grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5"
                        style={{ color: PURPLE }}
                      >
                        <Package size={18} />
                      </div>
                      <h2 className="text-base font-semibold leading-7 text-white">
                        Start Your Order
                      </h2>
                      <p className="mt-1 text-sm leading-6 text-white/50">
                        Fill out the order details to get started.
                      </p>
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium leading-6 text-white/70">
                          Order Title
                        </label>
                        <div className="mt-2">
                          <input
                            id="orderTitle"
                            type="text"
                            autoComplete="off"
                            aria-invalid={Boolean(orderErrors.title)}
                            value={orderTitle}
                            onChange={(e) => {
                              setOrderTitle(
                                DOMPurify.sanitize(e.target.value)
                              );
                              setOrderErrors((previous) => ({ ...previous, title: "" }));
                            }}
                            className="block w-full rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-white shadow-sm outline-none placeholder:text-white/40 focus:border-emerald-400/40 focus:ring-2 focus:ring-emerald-500/20 sm:text-sm"
                          />
                          {orderErrors.title && <p className="mt-2 text-xs text-red-300">{orderErrors.title}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium leading-6 text-white/70">
                          Days Needed
                        </label>
                        <div className="mt-2">
                          <input
                            id="daysNeeded"
                            type="number"
                            min="1"
                            max="6"
                            step="1"
                            inputMode="numeric"
                            aria-invalid={Boolean(orderErrors.daysNeeded)}
                            value={daysNeeded}
                            onChange={(e) => {
                              setDaysNeeded(
                                DOMPurify.sanitize(e.target.value)
                              );
                              setOrderErrors((previous) => ({ ...previous, daysNeeded: "" }));
                            }}
                            className="block w-full rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-white shadow-sm outline-none placeholder:text-white/40 focus:border-emerald-400/40 focus:ring-2 focus:ring-emerald-500/20 sm:text-sm"
                          />
                          {orderErrors.daysNeeded && <p className="mt-2 text-xs text-red-300">{orderErrors.daysNeeded}</p>}
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor="orderDescription"
                          className="block text-sm font-medium leading-6 text-white/70"
                        >
                          Order Description
                        </label>
                        <div className="mt-2">
                          <textarea
                            id="orderDescription"
                            rows={4}
                            maxLength={250}
                            aria-invalid={Boolean(orderErrors.description)}
                            value={orderDescription}
                            onChange={(e) => {
                              setOrderDescription(
                                DOMPurify.sanitize(e.target.value)
                              );
                              setOrderErrors((previous) => ({ ...previous, description: "" }));
                            }}
                            className="block w-full resize-none rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-white shadow-sm outline-none placeholder:text-white/40 focus:border-emerald-400/40 focus:ring-2 focus:ring-emerald-500/20 sm:text-sm"
                          />
                        </div>
                        <p className={`mt-2 text-xs ${orderErrors.description ? "text-red-300" : "text-white/40"}`}>
                          {orderErrors.description || `${orderDescription.trim().length}/250 characters. Minimum 100.`}
                        </p>
                      </div>

                      <div>
                        <label
                          htmlFor="price"
                          className="block text-sm font-medium leading-6 text-white/70"
                        >
                          Price (USD)
                        </label>
                        <div className="relative mt-2 rounded-md shadow-sm">
                          <input
                            id="price"
                            name="price"
                            type="number"
                            min="5"
                            step="0.01"
                            inputMode="decimal"
                            aria-invalid={Boolean(orderErrors.price)}
                            placeholder="0.00"
                            value={orderPrice}
                            onChange={(e) => {
                              setOrderPrice(
                                DOMPurify.sanitize(e.target.value)
                              );
                              setOrderErrors((previous) => ({ ...previous, price: "" }));
                            }}
                            className="block w-full rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-white shadow-sm outline-none placeholder:text-white/40 focus:border-emerald-400/40 focus:ring-2 focus:ring-emerald-500/20 sm:text-sm"
                          />
                          {orderErrors.price && <p className="mt-2 text-xs text-red-300">{orderErrors.price}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 -mx-5 -mb-5 mt-6 flex items-center justify-end gap-x-3 border-t border-white/10 bg-[#0b0a14]/95 px-5 py-4 backdrop-blur-xl">
                  <button
                    type="button"
                    onClick={closePopup}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/70 transition hover:bg-white/10 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingOrder}
                    className="rounded-2xl px-4 py-2 text-sm font-semibold text-black shadow-sm transition active:scale-95"
                    style={{
                      background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                      boxShadow: `0 10px 24px -10px ${MINT}`,
                    }}
                  >
                    {isCreatingOrder ? "Creating Order" : "Begin Order"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <section className="relative flex h-full w-full flex-col overflow-hidden bg-[#07060d]">
        <div
          className="pointer-events-none absolute -top-20 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: PURPLE, opacity: 0.08 }}
        />
        <div
          className="pointer-events-none absolute bottom-0 right-0 h-72 w-72 rounded-full blur-3xl"
          style={{ background: MINT, opacity: 0.06 }}
        />

        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-white/10 bg-[#07060d]/80 px-3 py-3 backdrop-blur-xl sm:px-5">
          <button
            type="button"
            onClick={onBack}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 md:hidden"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>

          {friendProfilePath ? (
            <Link
              to={friendProfilePath}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl transition hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
              aria-label={`View ${friendUsername}'s profile`}
            >
              <HeaderAvatar
                src={friendDetails.profileInfo?.profilePicUrl || ""}
                name={friendUsername}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">
                  @{friendUsername}
                </p>
                <p className="truncate text-[11px] text-white/50">
                  Tap to view profile
                </p>
              </div>
            </Link>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <HeaderAvatar src="" name="Conversation" />
              <p className="truncate text-sm font-semibold text-white">
                Conversation
              </p>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            {isEditor && (
              <button
                type="button"
                onClick={openPopup}
                className="hidden items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/10 sm:inline-flex"
              >
                <Package size={13} style={{ color: PURPLE }} />
                New order
              </button>
            )}
          </div>
        </header>

        <div className="relative flex-1 overflow-y-auto px-3 py-4 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent] sm:px-6">
          {messages.length === 0 ? (
            <div className="grid h-full place-items-center">
              <div className="text-center">
                <p className="text-sm font-medium text-white/70">
                  No messages yet
                </p>
                <p className="mt-1 text-xs text-white/40">
                  Say hi to start the conversation.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">{messages.map(renderMessage)}</div>
          )}
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSendMessage();
          }}
          className="sticky bottom-0 z-20 border-t border-white/10 bg-[#07060d]/80 px-3 pt-3 backdrop-blur-xl sm:px-5"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          }}
        >
          <div className="flex items-end gap-2 rounded-3xl border border-white/10 bg-white/[0.03] p-1.5 backdrop-blur-xl">
            <div className="flex-grow">
              {(videoFileError || selectedVideoFiles.length > 0) && (
                <div
                  className={`mb-2 rounded-2xl border px-3 py-2 text-xs ${
                    videoFileError
                      ? "border-red-400/20 bg-red-500/10 text-red-200"
                      : "border-emerald-400/20 bg-emerald-500/10 text-emerald-100"
                  }`}
                >
                  {videoFileError ||
                    (isUploadingVideos
                      ? `Uploading videos... ${videoUploadProgress}%`
                      : `${selectedVideoFiles.length} video${
                          selectedVideoFiles.length === 1 ? "" : "s"
                        } selected`)}
                </div>
              )}
              <div className="relative flex w-full items-center gap-3">
                <label
                  className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-2xl text-white/60 transition hover:bg-white/5 hover:text-white"
                  aria-label="Attach"
                  title="Attach videos"
                >
                  <input
                    key={fileInputResetKey}
                    type="file"
                    multiple
                    accept="video/mp4,video/quicktime,.mp4,.mov"
                    disabled={isUploadingVideos}
                    onChange={handleVideoFileChange}
                    className="sr-only"
                  />
                  <Paperclip size={17} />
                </label>
                {/* <button 
                  className="flex-shrink-0 text-lg hover:opacity-70"
                  onClick={openGifModal}
                >
                  GIF
                </button> */}
                <input
                  type="text"
                  className="min-w-0 flex-1 rounded-2xl bg-white/[0.04] px-3 py-2.5 text-[15px] text-white placeholder:text-white/40 outline-none transition focus:bg-white/[0.06] focus:ring-2 focus:ring-emerald-500/20"
                  placeholder="Message..."
                  disabled={isUploadingVideos}
                  value={newMessage}
                  onChange={(e) =>
                    setNewMessage(DOMPurify.sanitize(e.target.value))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={openPopup}
              className={`h-10 w-10 shrink-0 place-items-center rounded-2xl text-white/60 transition hover:bg-white/5 hover:text-white ${
                isEditor ? "grid" : "hidden"
              }`}
              aria-label="Create order"
            >
              <Package size={17} />
            </button>
            <div className="flex flex-row gap-x-2 items-center">
              <button
                type="submit"
                disabled={
                  isUploadingVideos ||
                  (!newMessage.trim() && selectedVideoFiles.length === 0)
                }
                className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-black transition active:scale-95 disabled:cursor-not-allowed disabled:bg-white/[0.06] disabled:text-white/30"
                style={
                  !isUploadingVideos &&
                  (newMessage.trim() || selectedVideoFiles.length > 0)
                    ? {
                        background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                        boxShadow: `0 10px 24px -10px ${MINT}`,
                      }
                    : undefined
                }
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </form>
      </section>
    </>
  );
};

export default ChatWindow;
